import { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import {
  pathConverter,
  iframePathPatterns,
  shouldNavigate,
  checkIframeOrigin,
  matchesIframeActivePath,
} from '../utils';
import {
  useIframeCacheInvalidation,
  useActiveIframePaths,
  useIframeDetailBreadcrumb,
} from '../hooks';
import styled from '@emotion/styled';
import { useNavigate, useLocation, useNavigation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DISABLE_IFRAME_BG_LOADING } from 'config';
import { Breadcrumbs } from 'components/breadcrumbs';
import { IconButton } from 'components/button';
import { Text } from 'components/text';
import { CornersIn, CornersOut } from '@phosphor-icons/react';
import { LinearProgress } from '@mui/material';
import { t } from '@lingui/macro';

type IframeProps = {
  path: string;
  onDetailFullscreenChange?: (active: boolean) => void;
};

export const Iframe = ({ path, onDetailFullscreenChange }: IframeProps) => {
  const [iframePath, setIframePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activePaths = useActiveIframePaths();
  const detailBreadcrumb = useIframeDetailBreadcrumb(path);
  const navigate = useNavigate();
  const navigation = useNavigation();

  const matchesPath = matchesIframeActivePath(path, activePaths);

  const hidden = !matchesPath;
  const iframeAreaRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const location = useLocation();
  const queryClient = useQueryClient();

  useIframeCacheInvalidation(iframePath, queryClient);

  useEffect(() => {
    if (
      (activePaths.includes(path) && iframePath !== path) ||
      activePaths.some(p => path.toLowerCase().includes(p)) ||
      iframePathPatterns.some(pattern => pattern.test(path))
    ) {
      if (path !== iframePath) {
        setLoading(true);
        // Hash-only iframe changes don't fire onLoad, so auto-clear after a timeout.
        if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => setLoading(false), 2000);
      }
      setIframePath(path);
    }
  }, [path, iframePath, activePaths]);

  // Clear timer on unmount
  useEffect(
    () => () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    },
    []
  );

  // When an entity is saved inline in the React table, force a reload if the iframe is
  // currently showing that entity's detail page (so the legacy SPA picks up the change).
  // Dispatchers fire `entity:updated` with `{ type: 'style' | 'item' | …, id: number }`.
  useEffect(() => {
    const handleEntityUpdated = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const { type, id } = e.detail ?? {};
      if (type != null && id != null && matchesIframeActivePath(`/${type}/${id}/`, activePaths)) {
        setReloadKey(prev => prev + 1);
        setLoading(true);
        if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => setLoading(false), 2000);
      }
    };
    window.addEventListener('entity:updated', handleEntityUpdated);
    return () => window.removeEventListener('entity:updated', handleEntityUpdated);
  }, [activePaths]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Preload /style iframe so any iframe page opens fast; loading one page loads the whole platform.
    if (!DISABLE_IFRAME_BG_LOADING) {
      timeoutId = setTimeout(() => {
        // Use functional update so an already-set deep-link path (e.g. from an email
        // link like /style/116/communicationlog) is not overwritten with the generic
        // /style preload path, which would interrupt the legacy SPA navigation and
        // cause the style list to appear empty on first load.
        setIframePath(prev => prev || '/style');
      }, 1000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    const iframeArea = iframeAreaRef.current;
    if (!iframeArea) {
      return;
    }
    const debouncedHandleResize = debounce(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    }, 200);
    const observer = new ResizeObserver(debouncedHandleResize);
    observer.observe(iframeArea);
    return () => {
      observer.disconnect();
    };
  }, [iframePath]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowed = checkIframeOrigin(event.origin);
      if (!allowed) {
        return;
      }
      if (event.data.type === 'relocateTo' && !hidden) {
        const newUrl = shouldNavigate(event.data.payload, location.pathname);
        if (newUrl) {
          navigate(newUrl);
        }
      }
    };
    window?.addEventListener('message', handleMessage);
    return () => {
      window?.removeEventListener('message', handleMessage);
    };
  }, [location, hidden, navigate]);

  useEffect(() => {
    if (detailBreadcrumb) return;
    if (fullscreen) {
      onDetailFullscreenChange?.(false);
    }
    setFullscreen(false);
  }, [detailBreadcrumb, fullscreen, onDetailFullscreenChange]);

  const handleDetailBack = () => {
    if (detailBreadcrumb) {
      navigate(detailBreadcrumb.backTo);
    }
  };

  const toggleFullscreen = () => {
    const next = !fullscreen;
    setFullscreen(next);
    onDetailFullscreenChange?.(next);
  };

  const isNavigating = navigation.state !== 'idle' && matchesPath;
  const showLoader = loading || isNavigating;

  return (
    <IframeWrapper hidden={hidden || !iframePath}>
      {detailBreadcrumb && iframePath ? (
        <DetailNavBar data-testid="iframe-detail-back-nav" data-entity={detailBreadcrumb.entity}>
          {!fullscreen ? (
            <Breadcrumbs
              crumbs={[
                {
                  text: detailBreadcrumb.listLabel,
                  link: detailBreadcrumb.backTo,
                },
                ...(detailBreadcrumb.name ? [{ lightText: detailBreadcrumb.name }] : []),
              ]}
              onBackClick={handleDetailBack}
            />
          ) : null}
          <DetailNavActions>
            <IconButton
              size="md"
              onClick={toggleFullscreen}
              data-testid="iframe-detail-fullscreen"
              aria-label={fullscreen ? t`Minimize` : t`Full screen`}
            >
              {fullscreen ? (
                <CornersIn data-testid="iframe-detail-minimize-icon" size={24} />
              ) : (
                <CornersOut data-testid="iframe-detail-fullscreen-icon" size={24} />
              )}
            </IconButton>
            <Text variant="bodySm">{fullscreen ? t`Minimize` : t`Full screen`}</Text>
          </DetailNavActions>
        </DetailNavBar>
      ) : null}
      {iframePath ? (
        <>
          <HidableIframe ref={iframeAreaRef}>
            <IframeEl
              title={path}
              id="iframe-old-spa"
              width={dimensions.width}
              height={dimensions.height}
              src={pathConverter(iframePath, reloadKey)}
              onLoad={() => {
                if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
                setLoading(false);
              }}
            />
          </HidableIframe>
          {showLoader ? <LoaderBar /> : null}
        </>
      ) : null}
    </IframeWrapper>
  );
};

const IframeEl = styled.iframe`
  border: none;
`;

const IframeWrapper = styled.div<{ hidden?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${p => (p.hidden ? '0 0 0' : '1 1 auto')};
  min-height: 0;
  height: ${p => (p.hidden ? '0' : '100%')};
  width: 100%;
`;

const DetailNavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const DetailNavBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding-bottom: 8px;
`;

const HidableIframe = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;
`;

// Thin progress bar pinned to the top of the iframe — conventional page-load indicator.
const LoaderBar = styled(LinearProgress)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
`;
